/**
 * 頁 10 — 庫存餘額（P1-6）
 *
 * · 顯示「而家邊個位置有幾多貨」；**cbm 係計費基礎**（箱／件只作對數用）
 * · ⭐ **倉內 cbm ＝ `warehouse` ＋ `damaged`**（`decisions/003` §4／§5：損壞貨未離開倉都照計租）
 *   → 呢個數就係租倉費基礎；「供應商未出貨」嘅 cbm 另列 `outsideCbm`
 * · ⭐ **帳本對沖行**（負數，例 `supplier`）默認收起 —— 行列表喺 `components/InventoryRows.tsx`
 * · 供應商只會見到自己嘅貨（後端 `supplierScope` 把關；前端**唔**傳 supplierId）
 * · ⭐ 帳本自洽：全部位置 cbm 總和應該 ≈ 0（每筆移動有出有入）→ 唔係就紅字警示（唔准靜靜）
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyState from '@coeldery/empty-state';
import FormSelectField from '@coeldery/form-select-field';
import { badge, card, grid2, hint as hintStyle, sectionTitle } from '@coeldery/ui-kit';
import { BASE_LOCATIONS, LocationChip, locationLabelKey } from '../components/StockChips';
import InventoryRows from '../components/InventoryRows';
import StockFlowTable from '../components/StockFlowTable';
import { ApiError, api, type BalanceRow, type InventorySummary, type Me, type Supplier } from '../lib/api';

/** 去掉浮點尾巴（顯示用；計數唔准前端再算） */
const fmt = (n: number): string => String(Math.round(n * 1e6) / 1e6);

interface Loaded {
  rows: BalanceRow[];
  summary: InventorySummary;
  scoped: boolean;
}

function SummaryPanel({ summary, scoped }: { summary: InventorySummary; scoped: boolean }) {
  const { t } = useTranslation();
  return (
    <>
      {!summary.invariantOk && (
        <p style={{ ...hintStyle, ...badge('warn'), display: 'block', fontWeight: 700 }} role="alert">
          {t('inv.invariantBad', { net: fmt(summary.netCbm) })}
        </p>
      )}
      <div style={grid2}>
        <div style={{ ...card, padding: '12px 14px' }}>
          <p style={{ ...hintStyle, margin: 0 }}>{t('inv.summaryWarehouse')}</p>
          <strong style={{ fontSize: '20px' }}>{fmt(summary.warehouseCbm)}</strong>
          <p style={hintStyle}>{t('inv.summaryWarehouseHint')}</p>
        </div>
        <div style={{ ...card, padding: '12px 14px' }}>
          <p style={{ ...hintStyle, margin: 0 }}>{t('inv.summaryDamaged')}</p>
          <strong style={{ fontSize: '20px' }}>{fmt(summary.damagedCbm)}</strong>
        </div>
        <div style={{ ...card, padding: '12px 14px' }}>
          <p style={{ ...hintStyle, margin: 0 }}>{t('inv.summaryCounts')}</p>
          <strong style={{ fontSize: '20px' }}>
            {summary.skuCount} / {summary.batchCount}
          </strong>
          <p style={hintStyle}>{t('inv.summaryCountsHint')}</p>
        </div>
      </div>
      {summary.byLocation.length > 0 && (
        <>
          <h3 style={sectionTitle}>{t('inv.byLocationTitle')}</h3>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {summary.byLocation.map((l) => (
              <li
                key={l.location}
                style={{ ...card, padding: '8px 12px', display: 'flex', gap: '8px', alignItems: 'center' }}
              >
                <LocationChip location={l.location} />
                <span style={hintStyle}>
                  {l.cartons} {t('inv.cartons')} · cbm {fmt(l.cbm)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {scoped && <p style={hintStyle}>{t('inv.scopedNotice')}</p>}
    </>
  );
}

export default function Inventory({ user }: { user: Me }) {
  const { t } = useTranslation();
  const [location, setLocation] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [includeEmpty, setIncludeEmpty] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [data, setData] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canPickSupplier = user.role === 'staff' || user.role === 'admin';

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.inventory({
        location: location || undefined,
        supplierId: canPickSupplier && supplierId ? supplierId : undefined,
        includeEmpty,
      });
      setData({ rows: res.rows ?? [], summary: res.summary, scoped: res.scoped });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [location, supplierId, includeEmpty, canPickSupplier, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!canPickSupplier) return;
    void api
      .suppliers()
      .then((res) => setSuppliers(res.suppliers ?? []))
      .catch(() => setSuppliers([]));
  }, [canPickSupplier]);

  const locationOptions = [
    { value: '', label: t('inv.allLocations') },
    ...BASE_LOCATIONS.map((l) => ({ value: l as string, label: t(locationLabelKey(l) ?? l) })),
  ];
  const supplierOptions = [
    { value: '', label: t('inv.allSuppliers') },
    ...suppliers.map((s) => ({ value: s.id, label: `${s.code}｜${s.name}` })),
  ];

  return (
    <>
      <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>{t('inv.title')}</h2>
      <p style={hintStyle}>{t('inv.intro')}</p>

      <div style={{ margin: '12px 0' }}>
        <FormSelectField
          id="f-inv-location"
          labelKey="inv.filterLocation"
          value={location}
          onChange={setLocation}
          options={locationOptions}
        />
      </div>
      {canPickSupplier && (
        <div style={{ margin: '0 0 12px' }}>
          <FormSelectField
            id="f-inv-supplier"
            labelKey="inv.filterSupplier"
            value={supplierId}
            onChange={setSupplierId}
            options={supplierOptions}
          />
        </div>
      )}
      <label style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', fontSize: '13px' }}>
        <input type="checkbox" checked={includeEmpty} onChange={(e) => setIncludeEmpty(e.target.checked)} />
        {t('inv.includeEmpty')}
      </label>

      {loading && <p style={hintStyle}>{t('common.loading')}</p>}
      {error && (
        <p style={{ ...hintStyle, color: 'var(--color-accent)', fontWeight: 700 }} role="alert">
          {error}{' '}
          <button
            type="button"
            onClick={() => void load()}
            style={{ border: 'none', background: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
          >
            {t('common.refresh')}
          </button>
        </p>
      )}

      {data && <SummaryPanel summary={data.summary} scoped={data.scoped} />}

      {/* ⭐ P1.5-A：直接顯示公式「入 − 出 ＝ 現時倉庫量」（三個角色同一組件；decisions/015 §2.3） */}
      <StockFlowTable />

      {data && data.rows.length === 0 && <EmptyState titleKey="inv.emptyTitle" bodyKey="inv.emptyBody" />}

      {data && data.rows.length > 0 && (
        <>
          <h3 style={sectionTitle}>{t('inv.rowsTitle')}</h3>
          {/* ⭐ 行列表拆去 `components/InventoryRows.tsx`（含「帳本對沖行」開關；頁面檔 ≤200 行） */}
          <InventoryRows rows={data.rows} role={user.role} />
        </>
      )}
    </>
  );
}
